import { useMemo, useState } from 'react';
import BottomSheet from './BottomSheet';
import './NhisHealthSnapshot.css';

const STATUS_META = {
  normal: { label: '정상', className: 'is-normal' },
  borderline: { label: '경계', className: 'is-borderline' },
  suspected: { label: '의심', className: 'is-suspected' },
  chronic: { label: '유질환', className: 'is-chronic' },
  no_data: { label: '미수검', className: 'is-muted' },
  not_applicable: { label: '비해당', className: 'is-muted' },
  unknown: { label: '확인필요', className: 'is-muted' },
};

const STATUS_PRIORITY = {
  suspected: 4,
  chronic: 4,
  borderline: 3,
  normal: 2,
  unknown: 1,
  no_data: 1,
  not_applicable: 1,
};
const MAIN_DOMAIN_ORDER = [
  '비만',
  '고혈압',
  '신장질환',
  '간장질환',
  '당뇨병',
  '만성신장질환',
  '빈혈',
  '시각',
  '청각',
  '폐결핵흉부질환',
];

const DOMAIN_ITEM_ORDER = {
  비만: ['신장', '체중', '허리둘레', '체질량지수'],
  고혈압: ['혈압(최고/최저)'],
  신장질환: ['요단백'],
  간장질환: ['에이에스티(AST, SGOT)', '에이엘티(ALT, SGPT)', '감마지티피(y-GTP)'],
  당뇨병: ['공복혈당'],
  만성신장질환: ['혈청크레아티닌', '신사구체여과율(GFR)'],
  빈혈: ['혈색소'],
  시각: ['시력(좌/우)'],
  청각: ['청각(좌/우)'],
  폐결핵흉부질환: ['폐결핵 흉부질환'],
};

const ITEM_DESCRIPTION = {
  공복혈당: '8시간 이상 공복 후 측정한 혈당이에요. 당뇨 진단과 혈당 조절 상태를 확인하는 기본 지표입니다.',
  혈청크레아티닌: '근육 대사 후 생기는 노폐물로, 신장이 얼마나 잘 걸러내는지 보여줘요. 수치가 높을수록 신장 기능 저하를 의심합니다.',
  '신사구체여과율(GFR)': '1분간 신장이 걸러내는 혈액량이에요. 신장 기능을 가장 직접적으로 나타내는 수치입니다.',
  혈색소: '혈액 속 산소를 운반하는 단백질이에요. 수치가 낮으면 빈혈을 의심하고, 피로감·어지러움과 연결될 수 있어요.',
  체질량지수: '키와 체중으로 계산한 비만도 지표예요(체중kg ÷ 키m²). 과체중·비만 여부를 빠르게 파악하는 데 사용합니다.',
  허리둘레: '복부 비만을 간편하게 확인하는 지표예요. 체중이 정상이어도 허리둘레가 높으면 대사 위험이 올라갑니다.',
  신장: '바르게 섰을 때 측정한 키예요. 체질량지수(BMI)를 계산하거나 체형 변화를 볼 때 함께 참고합니다.',
  체중: '몸의 무게를 측정한 값이에요. 비만도나 최근 변화 추이를 볼 때 함께 참고합니다.',
  '혈압(최고/최저)': '심장이 수축·이완할 때 혈관에 가해지는 압력이에요. 최고(수축기)/최저(이완기) 두 수치를 함께 봐야 합니다.',
  요단백: '소변에 단백질이 섞여 나오는지 확인해요. 신장 필터가 손상되면 단백질이 새어 나올 수 있어요.',
  '청각(좌/우)': '왼쪽과 오른쪽 귀의 청력 상태를 확인하는 검사예요. 좌우 차이나 이상 여부를 함께 봅니다.',
  '에이에스티(AST, SGOT)': '간세포 손상 시 혈액으로 흘러나오는 효소예요. 심장·근육에도 존재해서, 수치가 높으면 간 외 원인도 함께 확인합니다.',
  '에이엘티(ALT, SGPT)': '간에 주로 존재하는 효소예요. 손상 시 혈액으로 나와 간 염증·손상을 가장 먼저 나타냅니다.',
  '감마지티피(y-GTP)': '간·담도 이상과 음주 영향에 민감한 수치예요. 평소 음주량이 많다면 주의 깊게 봐야 할 수치예요.',
  '감마지티피(γ-GTP)': '간·담도 이상과 음주 영향에 민감한 수치예요. 평소 음주량이 많다면 주의 깊게 봐야 할 수치예요.',
};

const INFO_BUTTON_ITEMS = new Set(Object.keys(ITEM_DESCRIPTION));

function fmtDate(s) {
  const str = String(s ?? '');
  if (str.length === 8) return `${str.slice(0, 4)}.${str.slice(4, 6)}.${str.slice(6, 8)}`;
  return str;
}

function normalizeText(value) {
  return String(value ?? '').replace(/\s+/g, '').trim();
}

function normalizeDomainLabel(label) {
  const text = normalizeText(label);
  if (text === '시각이상' || text === '시각') return '시각';
  if (text === '청각이상' || text === '청각') return '청각';
  if (text === '폐결핵흉부질환') return '폐결핵흉부질환';
  if (MAIN_DOMAIN_ORDER.includes(text)) return text;
  return null;
}

function classifyMainDomain(item) {
  const disease = normalizeDomainLabel(item?.targetDisease);
  if (disease) return disease;

  const itemName = normalizeText(item?.item);
  if (!itemName) return null;

  if (itemName.includes('시력')) return '시각';
  if (itemName.includes('청각')) return '청각';
  if (itemName.includes('폐결핵')) return '폐결핵흉부질환';
  if (itemName === normalizeText('요단백')) return '신장질환';
  if (itemName === normalizeText('공복혈당')) return '당뇨병';
  if (itemName === normalizeText('혈색소')) return '빈혈';
  if (itemName === normalizeText('혈압(최고/최저)')) return '고혈압';
  if (itemName === normalizeText('혈청크레아티닌') || itemName === normalizeText('신사구체여과율(GFR)')) return '만성신장질환';
  if (
    itemName === normalizeText('에이에스티(AST, SGOT)')
    || itemName === normalizeText('에이엘티(ALT, SGPT)')
    || itemName === normalizeText('감마지티피(y-GTP)')
  ) return '간장질환';
  if (
    itemName === normalizeText('신장')
    || itemName === normalizeText('체중')
    || itemName === normalizeText('허리둘레')
    || itemName === normalizeText('체질량지수')
  ) return '비만';
  return null;
}

function sortItemsByDomain(domainLabel, items) {
  const order = DOMAIN_ITEM_ORDER[domainLabel] ?? [];
  const rankMap = new Map(order.map((name, idx) => [normalizeText(name), idx]));
  return [...items].sort((a, b) => {
    const aName = normalizeText(a?.item);
    const bName = normalizeText(b?.item);
    const aRank = rankMap.has(aName) ? rankMap.get(aName) : Number.MAX_SAFE_INTEGER;
    const bRank = rankMap.has(bName) ? rankMap.get(bName) : Number.MAX_SAFE_INTEGER;
    if (aRank !== bRank) return aRank - bRank;
    return String(a?.item ?? '').localeCompare(String(b?.item ?? ''), 'ko');
  });
}

function buildMainDomainGroups(items) {
  const grouped = new Map(MAIN_DOMAIN_ORDER.map((label) => [label, []]));
  (items ?? []).forEach((item) => {
    const domain = classifyMainDomain(item);
    if (!domain) return;
    grouped.get(domain)?.push(item);
  });

  return MAIN_DOMAIN_ORDER
    .map((domainLabel) => ({
      domainLabel,
      items: sortItemsByDomain(domainLabel, grouped.get(domainLabel) ?? []),
    }))
    .filter((domain) => domain.items.length > 0);
}

function metricStatusCode(item) {
  if (!item?.status?.code) return 'unknown';
  return item.status.code;
}

function normalizeStatusBucket(statusCode) {
  if (statusCode === 'suspected' || statusCode === 'chronic') return 'suspected';
  if (statusCode === 'borderline') return 'borderline';
  if (statusCode === 'normal') return 'normal';
  return 'unknown';
}

function hasMeaningfulValue(value) {
  return String(value ?? '').trim() !== '';
}

function formatValueWithUnit(value, unit) {
  if (!hasMeaningfulValue(value)) return '최근값 없음';
  return unit ? `${value} ${unit}` : String(value);
}

function shouldShowInfoButton(item) {
  return INFO_BUTTON_ITEMS.has(item?.item);
}

function getDisplayStatusLabel(item) {
  const code = item?.status?.code ?? 'unknown';
  const rawLabel = String(item?.status?.label ?? '').trim();
  const itemName = item?.item;

  if (itemName === '체질량지수') {
    if (code === 'suspected') return '비만 의심';
    if (code === 'borderline' && rawLabel) return rawLabel;
  }

  if (itemName === '허리둘레') {
    if (code === 'suspected') return '복부비만 의심';
    if (code === 'borderline' && rawLabel) return rawLabel;
  }

  if (rawLabel) return rawLabel;

  const meta = STATUS_META[code] ?? STATUS_META.unknown;
  return meta.label;
}

function domainItemSort(domainLabel, items) {
  const preferredOrder = domainLabel === '비만'
    ? ['체질량지수', '허리둘레', '체중', '신장']
    : (DOMAIN_ITEM_ORDER[domainLabel] ?? []);

  const rankMap = new Map(preferredOrder.map((name, idx) => [normalizeText(name), idx]));

  return [...items].sort((a, b) => {
    const aName = normalizeText(a?.item);
    const bName = normalizeText(b?.item);
    const aRank = rankMap.has(aName) ? rankMap.get(aName) : Number.MAX_SAFE_INTEGER;
    const bRank = rankMap.has(bName) ? rankMap.get(bName) : Number.MAX_SAFE_INTEGER;
    if (aRank !== bRank) return aRank - bRank;
    return String(a?.item ?? '').localeCompare(String(b?.item ?? ''), 'ko');
  });
}

function domainLatestDate(items) {
  const dates = items
    .map((item) => item?.latest?.date)
    .filter((d) => typeof d === 'string' && d.length === 8)
    .sort();
  return dates.length ? dates[dates.length - 1] : null;
}

function buildDomainCard(domain) {
  const dominant = domain.items.reduce((acc, item) => {
    const code = metricStatusCode(item);
    const score = STATUS_PRIORITY[code] ?? 0;
    if (!acc || score > acc.score) return { code, score };
    return acc;
  }, null);

  const normalizedStatus = normalizeStatusBucket(dominant?.code ?? 'unknown');
  const sortedItems = domainItemSort(domain.domainLabel, domain.items);

  return {
    domainLabel: domain.domainLabel,
    statusCode: normalizedStatus,
    itemCount: sortedItems.length,
    baseDate: domainLatestDate(sortedItems),
    items: sortedItems,
  };
}

function DomainStatusSection({ title, domains }) {
  const [selectedItem, setSelectedItem] = useState(null);

  if (!domains.length) return null;

  const sheetStatusCode = selectedItem?.status?.code ?? 'unknown';
  const sheetStatusMeta = STATUS_META[sheetStatusCode] ?? STATUS_META.unknown;
  const sheetStatusLabel = selectedItem ? getDisplayStatusLabel(selectedItem) : '';
  const sheetValue = selectedItem ? formatValueWithUnit(selectedItem?.latest?.value, selectedItem?.unit) : '';
  const sheetDescription = selectedItem ? ITEM_DESCRIPTION[selectedItem?.item] : '';

  return (
    <>
      <section className="nhis-domain-status-section">
        <div className="nhis-domain-status-head">
          <h4>{title}</h4>
          <span className="nhis-domain-status-count">{domains.length}개</span>
        </div>
        <div className="nhis-domain-status-grid">
          {domains.map((domain) => {
            return (
              <article key={`${domain.statusCode}-${domain.domainLabel}`} className="nhis-domain-status-card">
                <div className="nhis-domain-status-card-head">
                  <strong>{domain.domainLabel}</strong>
                </div>
                <div className="nhis-domain-item-list">
                  {domain.items.map((item) => {
                    const itemStatusCode = metricStatusCode(item);
                    const itemMeta = STATUS_META[itemStatusCode] ?? STATUS_META.unknown;
                    const itemStatusLabel = getDisplayStatusLabel(item);
                    const currentText = formatValueWithUnit(item?.latest?.value, item?.unit);
                    const suppressItemState = item.item === '체중' || item.item === '신장';
                    const showItemState = !suppressItemState;
                    const showInfoButton = shouldShowInfoButton(item);
                    return (
                      <div key={`${domain.domainLabel}-${item.item}`} className="nhis-domain-item-block">
                        <div className="nhis-domain-item-row">
                          <div className="nhis-domain-item-left">
                            <div className="nhis-domain-item-text">
                              <div className="nhis-domain-item-name-wrap">
                                <span className="nhis-domain-item-name">{item.item}</span>
                                {showInfoButton && (
                                  <button
                                    type="button"
                                    className="nhis-domain-item-info-button"
                                    onClick={() => setSelectedItem(item)}
                                    aria-label={`${item.item} 설명 보기`}
                                  >
                                    i
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="nhis-domain-item-right">
                            <span className="nhis-domain-item-value">{currentText}</span>
                            {showItemState && (
                              <span className={`nhis-domain-item-state ${itemMeta.className}`}>{itemStatusLabel}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="nhis-domain-status-card-meta">
                  {domain.baseDate ? `기준 ${fmtDate(domain.baseDate)}` : '기준일 없음'}
                  {` · ${domain.itemCount}개 항목`}
                </p>
              </article>
            );
          })}
        </div>
      </section>

      <BottomSheet
        open={Boolean(selectedItem)}
        onClose={() => setSelectedItem(null)}
        title={selectedItem?.item}
        subHeader={selectedItem ? (
          <div className="nhis-item-info-subheader">
            <span className="nhis-item-info-subvalue">{sheetValue}</span>
            <span className={`nhis-item-info-substatus ${sheetStatusMeta.className}`}>{sheetStatusLabel}</span>
          </div>
        ) : null}
      >
        {selectedItem && (
          <div className="nhis-item-info-sheet">
            {sheetDescription && (
              <section className="nhis-item-info-section">
                <h5>이 검사는 무엇인가요?</h5>
                <p>{sheetDescription}</p>
              </section>
            )}

            {(selectedItem?.reference?.normalA || selectedItem?.reference?.normalB || selectedItem?.reference?.suspected) && (
              <section className="nhis-item-info-section">
                <h5>기준 범위</h5>
                <div className="nhis-item-info-reference-list">
                  {selectedItem?.reference?.normalA && (
                    <div className="nhis-item-info-reference-row">
                      <span>정상 기준</span>
                      <strong>{selectedItem.reference.normalA}</strong>
                    </div>
                  )}
                  {selectedItem?.reference?.normalB && (
                    <div className="nhis-item-info-reference-row">
                      <span>경계 참고</span>
                      <strong>{selectedItem.reference.normalB}</strong>
                    </div>
                  )}
                  {selectedItem?.reference?.suspected && (
                    <div className="nhis-item-info-reference-row">
                      <span>의심 기준</span>
                      <strong>{selectedItem.reference.suspected}</strong>
                    </div>
                  )}
                </div>
              </section>
            )}

            {selectedItem?.availability?.reason && (
              <section className="nhis-item-info-section">
                <h5>참고</h5>
                <p>{selectedItem.availability.reason}</p>
              </section>
            )}
          </div>
        )}
      </BottomSheet>
    </>
  );
}

export default function NhisHealthSnapshot({ healthCheckupOverview }) {
  const commonItems = useMemo(
    () => (Array.isArray(healthCheckupOverview?.commonItems) ? healthCheckupOverview.commonItems : []),
    [healthCheckupOverview?.commonItems],
  );
  const optionalItems = useMemo(
    () => (Array.isArray(healthCheckupOverview?.optionalItems) ? healthCheckupOverview.optionalItems : []),
    [healthCheckupOverview?.optionalItems],
  );
  const allCheckupItems = useMemo(() => [...commonItems, ...optionalItems], [commonItems, optionalItems]);
  const mainDomains = useMemo(() => buildMainDomainGroups(allCheckupItems), [allCheckupItems]);
  const mainItems = useMemo(() => mainDomains.flatMap((domain) => domain.items), [mainDomains]);
  const domainSummaries = useMemo(() => mainDomains.map(buildDomainCard), [mainDomains]);

  const statusCount = useMemo(() => {
    const result = {
      borderline: 0,
      suspected: 0,
      normal: 0,
    };

    domainSummaries.forEach((domain) => {
      if (domain.statusCode === 'borderline') result.borderline += 1;
      else if (domain.statusCode === 'suspected') result.suspected += 1;
      else if (domain.statusCode === 'normal') result.normal += 1;
    });

    return result;
  }, [domainSummaries]);

  const statusGroupedDomains = useMemo(() => ({
    suspected: domainSummaries.filter((domain) => domain.statusCode === 'suspected'),
    borderline: domainSummaries.filter((domain) => domain.statusCode === 'borderline'),
    normal: domainSummaries.filter((domain) => domain.statusCode === 'normal'),
  }), [domainSummaries]);
  const attentionDomains = useMemo(
    () => [...statusGroupedDomains.suspected, ...statusGroupedDomains.borderline],
    [statusGroupedDomains],
  );

  const latestDate = useMemo(() => {
    const dates = mainItems
      .map((item) => item?.latest?.date)
      .filter((d) => typeof d === 'string' && d.length === 8)
      .sort();

    return dates.length ? dates[dates.length - 1] : null;
  }, [mainItems]);

  if (!mainItems.length) return null;

  return (
    <section className="nhis-snapshot-card">
      <div className="nhis-snapshot-header">
        <div>
          <h3>최근 검진 결과 요약</h3>
          <p>검진 항목별 최신 수치와 상태를 정리했습니다.</p>
        </div>
      </div>

      <div className="nhis-snapshot-summary-line">
        {latestDate && (
          <span className="nhis-summary-date">
            최근 검진일 <strong>{fmtDate(latestDate)}</strong>
          </span>
        )}
        <span className="nhis-summary-pill is-suspected">
          의심 <strong>{statusCount.suspected}</strong>
        </span>
        <span className="nhis-summary-pill is-borderline">
          경계 <strong>{statusCount.borderline}</strong>
        </span>
        <span className="nhis-summary-pill is-normal">
          정상 <strong>{statusCount.normal}</strong>
        </span>
      </div>

      <div className="nhis-domain-status-wrap">
        <DomainStatusSection
          title="확인 필요 항목"
          domains={attentionDomains}
        />

        <DomainStatusSection
          title="정상 항목"
          domains={statusGroupedDomains.normal}
        />

        {attentionDomains.length === 0 && statusGroupedDomains.normal.length === 0 && (
          <div className="nhis-empty-block">현재 표시할 검진 항목이 없습니다.</div>
        )}
      </div>
    </section>
  );
}
